/*
 * Datazoom, Inc ("COMPANY") CONFIDENTIAL
 * Copyright (c) 2017-2018 [Datazoom, Inc.], All Rights Reserved.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 */

import { call, put, takeLatest, all, takeLeading } from "redux-saga/effects";
import { messages } from "../../app-old/utilities/messages";
import * as authAction from "./actions";
import * as userAction from "../user/actions";
import { getLSAuth } from "../../app-old/utilities/utils";
import {
  RequestLogin,
  ForgotPassword,
  ChangePassword,
  ResetPassword,
  SignupUser,
  CompleteSignup,
  CardAdd,
  FetchCardDetails,
  FetchUserDetails,
  RequestLogout,
  SocialSignup,
  RefreshHelpSite,
  CheckEligibilitySSO,
  SSOCallback,
} from "./services";
import { FetchAUser } from "../user/services";
import { BILLING_ADMIN, CUSTOMER_ADMIN } from "../../app/Constants/Constants";
import { storeSessionDetails } from "./helper";

// function that makes the api request and returns a Promise for response
export function* checkAuthSaga() {
  try {
    const loggedInData = getLSAuth();
    let response;
    /* istanbul ignore if */
    if (loggedInData && loggedInData.user_id) {
      yield put(authAction.LoginSuccess(loggedInData));
      response = yield call(
        FetchUserDetails(
          loggedInData.roleName === CUSTOMER_ADMIN ||
            loggedInData.roleName === BILLING_ADMIN
            ? loggedInData.companyuuid
            : undefined,
        ),
      );
    }

    /* istanbul ignore if */
    if (
      response &&
      response.data &&
      response.data.data &&
      response.data.data.accountStatus
    ) {
      loggedInData.customerAccountStatus = response.data.data.accountStatus;
      localStorage.setItem("session_details", JSON.stringify(loggedInData));
      yield put(authAction.LoginSuccess(loggedInData));
    }
  } catch (e) {
    // console.log("check auth error", e);
  }
}
// worker saga: makes the api call when watcher saga sees the action
export function* loginSaga(action) {
  try {
    const response = yield call(RequestLogin, action.payload);
    const loggedInData = response && response.data ? response.data : null;
    if (loggedInData && loggedInData.success) {
      const userDetails = yield call(FetchAUser);
      const loggedUserDetails =
        userDetails && userDetails.data ? userDetails.data.data : null;
      if (userDetails.data && userDetails.data.success) {
        yield put(
          authAction.LoginSuccess(storeSessionDetails(loggedUserDetails)),
        );
        yield put(userAction.FetchAUserSuccess(userDetails.data.data));
      }
    } else {
      yield put(
        authAction.LoginError({
          message: response && response.data && response.data.message,
        }),
      );
    }
  } catch (error) {
    yield put(
      authAction.LoginError(
        error && error.response && error.response.status === 429
          ? { message: messages.too_many_requests }
          : error,
      ),
    );
  }
}

export function* logoutSaga() {
  /* istanbul ignore next */
  try {
    const response = yield call(RequestLogout);
    if (response && response.data && response.data.success) {
      yield put(authAction.LogoutSuccess());
    }
    localStorage.removeItem("session_details");
    localStorage.removeItem("company-status");
  } catch (e) {
    // console.log("logout error", e);
  }
}

export function* forgotPasswordSaga(action) {
  try {
    const response = yield call(ForgotPassword, action.payload);

    const forgotPasswordData = response ? response.data : null;

    if (forgotPasswordData && forgotPasswordData.success) {
      yield put(authAction.ForgotPasswordSuccess(forgotPasswordData));
    } else {
      yield put(authAction.ForgotPasswordRejected(forgotPasswordData));
    }
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(authAction.ForgotPasswordRejected(error));
  }
}

export function* changePasswordSaga(action) {
  try {
    const response = yield call(ChangePassword, action.payload);
    const changePasswordData = response.data ? response.data : null;
    yield put(authAction.ChangePasswordSuccess(changePasswordData));
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(authAction.ChangePasswordRejected(error));
  }
}

export function* resetPasswordSaga(action) {
  try {
    const response = yield call(ResetPassword, action.payload);
    const resetPasswordData = response.data ? response.data : null;
    if (resetPasswordData && resetPasswordData.success) {
      yield put(authAction.ResetPasswordSuccess(resetPasswordData));
      /* istanbul ignore next */
      if (resetPasswordData.data) {
        yield put(
          authAction.RequestLogin({
            email: resetPasswordData.data,
            password: action.payload.password,
          }),
        );
      }
    } else {
      yield put(authAction.ResetPasswordRejected(resetPasswordData));
    }
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(authAction.ResetPasswordRejected(error));
  }
}
export function* signupSaga(action) {
  try {
    const response = yield call(SignupUser, action.payload);
    /* istanbul ignore else */
    if (response && response.data && response.data.success) {
      yield put(authAction.SignupSuccess(response.data));
    } else if (response && response.data && response.data.message) {
      yield put(authAction.SignupFailed(response.data));
    } else {
      yield put(
        authAction.SignupFailed({
          message: "Something went wrong",
        }),
      );
    }
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(authAction.SignupFailed(error));
  }
}
export function* completeSignupSaga(action) {
  try {
    const response = yield call(CompleteSignup, action.payload);
    if (response && response.data && response.data.success) {
      if (response.data.data.status === "PENDING") {
        yield put(
          authAction.RedirectToPendingUserView(response.data.data.companyName),
        );
      } else {
        yield put(
          authAction.RequestLogin({
            email: response.data.data.email,
            password: action.payload.password,
          }),
        );
      }
      yield put(authAction.CompleteSignupSuccess(response.data));
    } else {
      yield put(
        authAction.CompleteSignupFailed({
          message: response.data.message,
          success: response.data.success,
          isExist:
            response.data && response.data.data && response.data.data.isExist,
        }),
      );
    }
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(authAction.CompleteSignupFailed(error));
  }
}
export function* cardAddSaga(action) {
  try {
    const response = yield call(CardAdd, action.payload);
    const cardAddData = response.data ? response.data : null;
    if (cardAddData && cardAddData.success) {
      // if (cardAddData.success) {
      yield put(authAction.CardAddSuccess(cardAddData));
      // } else {
      //   yield put(authAction.CardAddFailed(cardAddData.message));
      // }
    } else {
      yield put(authAction.CardAddFailed(cardAddData));
    }
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(authAction.CardAddFailed(error));
  }
}
export function* fetchCardSaga(action) {
  try {
    const response = yield call(FetchCardDetails, action.payload);
    const cardData = response && response.data ? response.data : {};
    const reqData = cardData.data ? cardData.data : cardData;
    yield put(
      cardData.success
        ? authAction.FetchCardDetailsSuccess(reqData)
        : authAction.FetchCardDetailsFailed(reqData),
    );
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(authAction.FetchCardDetailsFailed(error));
  }
}

export function* socialSigupSaga(action) {
  try {
    const response = yield call(SocialSignup, action.payload);
    /* istanbul ignore else */
    if (response && response.data && response.data.success) {
      const userDetails = yield call(FetchAUser);
      const loggedUserDetails =
        userDetails && userDetails.data ? userDetails.data.data : null;
      if (userDetails.data && userDetails.data.success) {
        yield put(
          authAction.LoginSuccess(storeSessionDetails(loggedUserDetails)),
        );
        yield put(userAction.FetchAUserSuccess(userDetails.data.data));
        yield put(authAction.SocialSignupSuccess(response.data));
      }
    } else {
      yield put(authAction.SocialSignupFailed(response.data));
      yield put(
        authAction.LoginError({
          message: response?.data?.message,
        }),
      );
    }
  } catch (error) {
    yield put(authAction.SocialSignupFailed(error));
    yield put(authAction.LoginError(error));
  }
}

export function* refreshHelpSiteSaga() {
  try {
    const response = yield call(RefreshHelpSite);
    if (response && (response?.success || response?.data?.success)) {
      yield put(
        authAction.RefreshHelpSiteSuccess(
          response?.message || response?.data?.message,
        ),
      );
    } else {
      yield put(authAction.RefreshHelpSiteFailed(response?.data?.message));
    }
  } catch (error) {
    yield put(authAction.RefreshHelpSiteFailed(error));
  }
}
export function* checkEligibilitySSOSaga(action) {
  try {
    const response = yield call(CheckEligibilitySSO, action.payload);

    if (response?.data?.success) {
      yield put(authAction.CheckEligibilitySSOSuccess(response.data));
    } else {
      yield put(authAction.CheckEligibilitySSOFailed(response.data));
    }
  } catch (error) {
    yield put(authAction.CheckEligibilitySSOFailed(error));
  }
}

export function* ssoCallBackSaga(action) {
  try {
    const response = yield call(SSOCallback, action.payload);
    const { success, data, message } = response.data || {};

    if (!success) {
      yield put(authAction.SSOCallbackFailed(response.data));
      return;
    }

    localStorage.removeItem("providerDetails");

    const jwt = data?.jwt;
    if (!jwt) {
      yield put(authAction.SSOCallbackSuccess(response.data));
      return;
    }
    const userDetails = yield call(FetchAUser);
    const userData = userDetails?.data?.data;
    const userSuccess = userDetails?.data?.success;

    if (userSuccess && userData) {
      yield put(authAction.LoginSuccess(storeSessionDetails(userData)));
      yield put(userAction.FetchAUserSuccess(userData));
      yield put(authAction.SSOCallbackSuccess(response.data));
    } else {
      yield put(authAction.SSOCallbackFailed(response.data));
      yield put(authAction.LoginError({ message }));
    }
  } catch (error) {
    yield put(authAction.SSOCallbackFailed(error));
    yield put(authAction.LoginError(error));
  }
}
// watcher saga: watches for actions dispatched to the store, starts worker saga
export function* authWatcher() {
  yield all([
    takeLatest(authAction.types.REQUEST_LOGIN, loginSaga),
    takeLatest(authAction.types.REQUEST_LOGOUT, logoutSaga),
    takeLatest(authAction.types.CHECK_AUTH, checkAuthSaga),
    takeLeading(authAction.types.FORGOT_PASSWORD, forgotPasswordSaga),
    takeLatest(authAction.types.CHANGE_PASSWORD, changePasswordSaga),
    takeLatest(authAction.types.RESET_PASSWORD, resetPasswordSaga),
    takeLatest(authAction.types.SIGNUP_USER, signupSaga),
    takeLatest(authAction.types.COMPLETE_SIGNUP, completeSignupSaga),
    takeLatest(authAction.types.CARD_ADD, cardAddSaga),
    takeLatest(authAction.types.FETCH_CARD_DETAILS, fetchCardSaga),
    takeLatest(authAction.types.SOCIAL_SIGNUP, socialSigupSaga),
    takeLatest(authAction.types.REFRESH_HELP_SITE, refreshHelpSiteSaga),
    takeLatest(authAction.types.CHECK_ELIGIBILITY_SSO, checkEligibilitySSOSaga),
    takeLatest(authAction.types.SSO_CALLBACK, ssoCallBackSaga),
  ]);
}
// REF: https://hackernoon.com/redux-saga-tutorial-for-beginners-and-dog-lovers-aa69a17db645
// notice the function* syntax. This creates a special kind of function new to ES6 called a generator.
// refer
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/function*
// example for fuction generator....
// --------------------------------------
// function* idMaker() {
//   let index = 0;
//   while (index < index+1)
//     yield index++;
// }
//
// let gen = idMaker();
//
// console.log(gen.next().value); // 0
// console.log(gen.next().value); // 1
// console.log(gen.next().value); // 2
// console.log(gen.next().value); // 3
// ...
